import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "210",
  "slug": "teachers-using-geography-games-classroom",
  "title": "How to Use Geography Games in the Classroom",
  "excerpt": "A classroom activity built around observing an image, making a guess, and explaining the result, with a one-screen option for shared devices.",
  "tldr": "Use a geography round as a short observation exercise. Ask students to write down evidence before guessing, discuss the revealed location, and finish with an individual explanation. A shared screen and paper answers work if students do not have devices.",
  "date": "2026-03-03",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Show the class a satellite image and ask everyone to write down two things they can see before naming a country. “Rectangular fields” is an observation. “The United States” is a guess. Keep those separate on the board so students can explain how they moved from one to the other."
    },
    {
      "type": "paragraph",
      "text": "The activity below is a suggested lesson format. Choose a location that fits the topic you are teaching, or use a game round for a broader exercise in observation."
    },
    {
      "type": "heading",
      "text": "Start with one shared round"
    },
    {
      "type": "paragraph",
      "text": "Project the image and give students a minute to inspect it silently. Then let pairs compare notes and choose a location. Ask them to record one alternative they considered and why they rejected it."
    },
    {
      "type": "paragraph",
      "text": "You can collect answers on paper or use a map on the board. If you plan to use individual devices and multiplayer, test the joining process and school network beforehand. Leave time to discuss the image rather than spending the whole lesson setting up the game."
    },
    {
      "type": "heading",
      "text": "Use the reveal to check the reasoning"
    },
    {
      "type": "paragraph",
      "text": "After showing the answer, return to the observations on the board. Which were accurate? Which supported more than one possible location? Which depended on a detail too small to see?"
    },
    {
      "type": "paragraph",
      "text": "A student who guessed the right country may have used an unreliable rule. A student who guessed incorrectly may have correctly identified a river delta or an irrigated field. Discuss those observations separately from the score."
    },
    {
      "type": "heading",
      "text": "Connect the image to a lesson objective"
    },
    {
      "type": "list",
      "items": [
        "For land use, ask students to mark fields, roads, and settlements, then describe how they are arranged.",
        "For rivers, trace the main channel and identify where it branches or bends. Compare the image with a diagram from the lesson.",
        "For climate, ask what the vegetation suggests, then check a climate graph. One green image cannot establish year-round rainfall.",
        "For map skills, locate the answer on a labelled map and describe its position relative to a coast or neighbouring country."
      ]
    },
    {
      "type": "heading",
      "text": "Make room for quieter students"
    },
    {
      "type": "paragraph",
      "text": "Collect a written observation before inviting spoken answers. In pairs, alternate who explains the guess so one confident player does not answer every round. You can also award credit for a supported explanation without keeping a public leaderboard."
    },
    {
      "type": "paragraph",
      "text": "For students who find the image difficult to read, provide a larger crop or label a few features to examine. A class-wide discussion can start with the same location even when students need different amounts of visual support."
    },
    {
      "type": "heading",
      "text": "Finish with an individual check"
    },
    {
      "type": "paragraph",
      "text": "Show a second image that contains the feature you discussed. Ask students to identify it and explain the visible evidence in two sentences. This gives you something to assess beyond whether a team won."
    },
    {
      "type": "paragraph",
      "text": "Keep the first session short. One round with time for explanations gives you a clearer view of students' reasoning than a sequence of guesses with no discussion."
    }
  ]
};
